import React from "react";
import { useState, useCallback } from "react";
import { useFollowup } from "../../Context/FollowupContext";
import { useData } from "../../Context/DataContext";
import { useAuth } from "../../Context/AuthContext";
import { useIntake } from "../../Context/IntakeContext";
import ButtonComponent from "../Inputs/ButtonComponent";
import { DateUtilityFunctions } from "../../Utils/DateUtilityFunctions";
import { debounce } from "@mui/material";
import {XCircle, CheckCircle, Star} from 'react-feather'

const BellReminderPopup = () => {
  const {currentUser} = useAuth()
  
  const {sendNewNotes} = useData()

  const { yellowStripeRemindersData,fetchYellowStripeRemindersData, setShowBellReminder, showBellReminder, handleDismissBellReminder } = useIntake();

  const [inputNotes, setInputNotes] = useState<{ [key: string]: string }>({})

  const formatDateToPST = (dateString: string) => {
    const date = new Date(dateString);

    // Convert date to PST (UTC-8 or UTC-7 depending on DST)
    const utcOffset = date.getTimezoneOffset(); // Get the current UTC offset in minutes
    const pstOffset = 8 * 60; // PST is UTC-8 hours
    const dstOffset = date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }).includes('PDT') ? 60 : 0;

    // Calculate the total offset and adjust the date
    const totalOffset = pstOffset - utcOffset + dstOffset;
    const pstDate = new Date(date.getTime() - (totalOffset * 60 * 1000));

    // Format the date to YYYY-MM-DD
    const year = pstDate.getFullYear();
    const month = String(pstDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(pstDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const debouncedSetInputNotes = useCallback(
    debounce((intakeId: string, value: string) => {
      setInputNotes(prevNotes => ({
        ...prevNotes,
        [intakeId]: value
      }));
    }, 300),
    [setInputNotes]
  );

  const handleInputChange = useCallback((intakeId: string, value: string) => {
    debouncedSetInputNotes(intakeId, value);
  }, [debouncedSetInputNotes]);


  const handleSubmit = async (intakeId: string) => {
    const note = inputNotes[intakeId];

    const data = {
      intake_id: intakeId,
      date: DateUtilityFunctions.getCurrentDateString(),
      notes: note,
      coordinator: currentUser.userId
    }
    if (!note) return; // Don't submit if the note is empty
  
    try {

      await sendNewNotes(data);
      
      // Clear the input after successful submission
      setInputNotes(prevNotes => ({
        ...prevNotes,
        [intakeId]: ''
      }));

      fetchYellowStripeRemindersData()
  
      // Optionally, you might want to refetch the reminders or update the local state
      // await fetchYellowStripeRemindersData();
    } catch (error) {
      console.error("Failed to submit note:", error);
      // Handle error (e.g., show an error message to the user)
    }
  };

  // const handleShowBell = () => {
  //   setShowBellReminder(!showBellReminder)
  // }
  return (
    <div className='absolute flex justify-center items-center align-middle inset-0 text-center w-full z-50'>
      
      <div className='relative h-3/5 w-3/4 flex flex-col gap-5 bg-dark-card border border-dark-border overflow-y-auto shadow-2xl rounded-xl p-4'>
        <div className="relative">
          <XCircle onClick={handleDismissBellReminder} className="fixed z-80" size={25} color='red'/>
        </div>
        {yellowStripeRemindersData && yellowStripeRemindersData.length > 0 
        ? (yellowStripeRemindersData.map((reminder, index) => {
          return (
              <div key={index} className='flex reminder justify-between items-center flex-row border border-dark-border rounded-xl p-4 bg-dark-input'>
                <p className="text-white font-medium">{reminder.name}</p>
                <p className="text-gray-400">Last note submitted: {formatDateToPST(reminder.most_recent_date)}</p>
                <textarea
                  className="flex bg-dark-input border border-dark-border min-w-80 text-white placeholder-gray-500 p-2 rounded-xl focus:outline-none focus:border-accent-teal"
                  placeholder="Input Note..."
                  value={inputNotes[reminder.intake_id || '']}
                  onChange={(e) => handleInputChange(reminder.intake_id, e.target.value)}
                />
                <div onClick={() => handleSubmit(reminder.intake_id)} className="flex hover:cursor-pointer">
                  <p className='text-center px-4 py-2 bg-gradient-to-r from-accent-teal to-accent-deep-purple rounded-xl font-semibold text-white hover:opacity-90 transition-opacity'>Submit</p>
                </div>
              </div>
          );
        })
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-8">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-yellow-400/20 via-amber-500/20 to-yellow-400/20 rounded-full blur-xl animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-yellow-400 to-amber-500 p-4 rounded-full shadow-lg shadow-yellow-500/30">
                <CheckCircle size={48} className="text-dark-bg" strokeWidth={2.5} />
              </div>
            </div>
            <div className="mt-6 flex items-center gap-2">
              <Star size={16} className="text-yellow-400 fill-yellow-400" />
              <h3 className="text-2xl font-bold bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-300 bg-clip-text text-transparent">
                All Caught Up!
              </h3>
              <Star size={16} className="text-yellow-400 fill-yellow-400" />
            </div>
            <p className="mt-3 text-gray-400 text-center max-w-sm">
              You're up to date on all Yellow Stripe follow-ups. Great work keeping track of your leads!
            </p>
            <div className="mt-6 flex items-center gap-3 px-5 py-2.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <span className="text-yellow-400 text-sm font-medium">No pending reminders</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BellReminderPopup;
